// AI进宝 - 研究模式
import { default as httpInstance } from '@/jmHttp/index';
import { aiResearchMode, researchNews } from '@/jmHttp/interface';

/**
 * 获取研究模式资料库列表。
 * @param {object} data 查询参数
 * @param {object} [config] 请求配置
 */
export const getResourceLibraryList = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.getResourceLibraryList, data, config);
};

/**
 * 初始化当前登录用户的研究模式工作区配置。
 * 无需业务入参；由全局登录后流程静默触发，不阻塞页面资源加载。
 * @param {object} [config] 请求配置
 */
export const initResearchWorkspaceApi = async (config = {}) => {
  return await httpInstance.post(aiResearchMode.workspaceInit, void 0, config);
};

// 创建文件夹
export const createFolderApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.createFolder, data, config);
};

// 创建空白文件（md 等普通格式）
export const createFileApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.createFile, data, config);
};

// 创建空白 Excel 文件
export const createExcelApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.createExcel, data, config);
};

// 创建空白 Word 文件
export const createDocxApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.createDocx, data, config);
};

// 创建空白 PPT 文件
export const createPptApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.createPpt, data, config);
};

// 移动文档节点
export const moveNodeApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.moveNode, data, config);
};

// 查询研究任务预设文件列表（taskType: 1 个股 / 2 行业）
export const getTaskPresetFilesApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.getTaskPresetFiles, data, config);
};

// 删除节点
export const deleteNodeApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.deleteNode, data, config);
};

// 更新任务（重命名/归档）
export const updateTaskApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.updateTask, data, config);
};

// 更新文档节点（重命名）
export const updateNodeApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.updateNode, data, config);
};

/**
 * 查询单个资料节点详情。
 * @param {{ id: number|string }} data
 */
export const getNodeDetailApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.nodeDetail, data, config);
};

/**
 * 查询全部收藏文件。
 * @param {object} [data] 查询参数，当前契约为空对象
 * @param {object} [config] 请求配置
 */
export const getNodeFavoriteListApi = async (data = {}, config = {}) => {
  return await httpInstance.post(aiResearchMode.nodeFavoriteList, data, config);
};

/**
 * 查询最近浏览文件，一次拉取最多 50 条。
 * @param {{ page?: number, pageSize?: number }} data 查询参数
 * @param {object} [config] 请求配置
 */
export const getNodeRecentListApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.nodeRecentList, data, config);
};

/**
 * 更新文件收藏状态。
 * @param {{ docNodeId: number|string, favorite: boolean }} data 目标节点与目标状态
 * @param {object} [config] 请求配置
 */
export const toggleNodeFavoriteApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.nodeFavoriteUpdate, data, config);
};

/**
 * 记录文件浏览。
 * @param {{ docNodeId: number|string }} data 目标节点
 * @param {object} [config] 请求配置
 */
export const recordNodeViewApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.nodeViewRecord, data, config);
};

/**
 * 石墨内容保存完成后触达资料节点，刷新服务端 contentUpdatedAt。
 * @param {{ docNodeId: number|string }} data
 */
export const touchNodeShimoContentApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.nodeShimoContentTouch, data, config);
};

/**
 * ONLYOFFICE 内容保存完成后触达资料节点，刷新服务端 contentUpdatedAt。
 * @param {{ docNodeId: number|string }} data - 资料节点 ID 参数
 * @param {object} [config] - 请求配置
 * @returns {Promise<object>} 接口响应
 */
export const touchOnlyOfficeContentApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.onlyOfficeContentTouch, data, config);
};

/**
 * 批量记录用户查看（预览）过某些资料节点 / 标记其 AI 修改为已读。
 *
 * 由中间编辑区在打开 / 预览文件时统一收口调用（左侧资料库、右侧 AI 会话区的预览都经此）。
 * 接口为批量形态：`nodeIds` 为节点 id 数组，单文件预览时传单元素数组即可。
 *
 * @param {object} data
 * @param {Array<number|string>} data.nodeIds 资料节点 id 列表
 * @param {number} [data.observedAt] 查看时间戳（毫秒），缺省由调用方补当前时间
 * @param {object} [config] 请求配置
 * @returns {Promise<{ code: string, msg?: string, data?: boolean }>}
 */
export const markNodeAiModifiedReadApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.nodeAiModifiedReadBatch, data, config);
};

// 上传文件（单文件，multipart/form-data）
export const uploadFileApi = async (formData, config = {}) => {
  return await httpInstance.post(aiResearchMode.uploadFile, formData, config);
};

// 批量上传文件（multipart/form-data，files 字段多值）
export const batchUploadFileApi = async (formData, config = {}) => {
  return await httpInstance.post(aiResearchMode.batchUploadFile, formData, config);
};

// 搜索文档节点
export const searchNodeApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.searchNode, data, config);
};

// 创建研究任务
export const createTaskApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.createTask, data, config);
};

/**
 * 本地文件已上传至 OSS 后，把它转存到研究模式资料库
 * @param {{ fileUrl: string, fileName: string, fileType?: string, fileSize?: number }} data
 */
export const ingestResearchModelDocumentFromOss = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.ingestFromOss, data, config);
};

/**
 * 把已有云文档转存到研究模式资料库
 * @param {{ documentId: string | number, name?: string, fileType?: string, source?: number }} data
 */
export const ingestResearchModelDocumentFromCloudDoc = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.ingestFromCloudDoc, data, config);
};

/**
 * 输入框本地文件附件转存到资料库
 * @param {{ taskId?: number | string, fileUrl: string, name?: string, temporaryLink?: boolean }} data
 */
export const nodeArchiveUploadApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.nodeArchiveUpload, data, config);
};

/**
 * 输入框云文档批量归档导入资料库
 * @param {{ l1FolderId?: number | string, documentIds: number[] }} data
 *   l1FolderId：资料库一级文件夹节点 id；documentIds：云文档 ID 列表
 */
export const nodeBatchArchiveImportCloudDocApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.nodeBatchArchiveImportCloudDoc, data, config);
};

/**
 * 查询资料节点导入进度。
 * @param {{ nodeId: number|string }} data
 */
export const getNodeImportProgressApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.nodeImportProgress, data, config);
};

/**
 * 按 docNodeId 数组批量反查节点 fullPath
 *
 * @param {object} data
 * @param {Array<number>} data.docNodeIds
 * @param {object} [config]
 * @returns {Promise<{ data: Array<{ docNodeId: number, fullPath: string }> }>}
 */
export const queryResearchDocPathsByIds = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.nodeFullPathBatch, data, config);
};

/**
 * 按 fullPaths 数组批量反查节点 { docNodeId, nodeType, fullPath, ownerId }
 *
 * 与 {@link queryResearchDocPathsByIds} 互为反向接口：
 *   - 反向 (id → path) 用于「发送」链路拼 query.header；
 *   - 正向 (path → id) 用于「历史回放」链路把 `<path>` 标签反解为可点击 chip。
 *
 * 响应中的 `nodeType` 与 `RESEARCH_MODEL_DOCUMENT_NODE_TYPE` 对齐（1=文件夹，2=文件），
 * 调用方可通过 `resolveResearchModelDocKind(nodeType)` 派生出 chip 所需的 kind。
 *
 * @param {{ fullPaths: string[], ownerId?: number }} data
 * @param {object} [config]
 * @returns {Promise<{ data: Array<{ ownerId?: number, fullPath: string, docNodeId: number, nodeType: number }> }>}
 */
export const queryResearchDocNodeIdsByFullPaths = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.nodeIdByFullPathBatch, data, config);
};

// 置顶一级文件夹
export const pinTaskFolderApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.pinTaskFolder, data, config);
};

// 取消置顶一级文件夹
export const unpinTaskFolderApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.unpinTaskFolder, data, config);
};

/**
 * 批量从云文档导入资料
 * @param {{ parentId: number, documentIds: number[] }} data
 */
export const batchImportCloudDocApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.batchImportCloudDoc, data, config);
};

/**
 * 按 messageIds 查询本轮会话的 API 审计摘要。
 *
 * 用于研究模式「流式结束」后判断本轮 assistant 产出过程中有哪些文件被新增 / 修改：
 * 入参 `messageIds` 传「用户提问的 message id」（即本轮 assistant 回答的 parentID），
 * 后端据此聚合该轮问答的操作记录；返回每条操作记录（含 operationType / docNodeId /
 * nodeType 等），由前端按 operationType（CREATE/EDIT）+ nodeType（文件）过滤聚合出
 * 「被修改文件汇总卡片」。
 *
 * @param {object} data
 * @param {Array<string>} data.messageIds 用户提问的 message id 列表（本轮 assistant 回答的 parentID）
 * @param {object} [config] 请求配置
 * @returns {Promise<{ data: Array<{ id: string, requestId: string, messageId: string, apiPath: string,
 *   operationType: number, operationTypeName: string, docNodeId: string, docNodeName: string,
 *   nodeType: number, fileType: number, ext: string, status: number, statusName: string,
 *   errorMsg: string, ctime: number, utime: number }> }>}
 */
export const queryResearchApiAuditSummary = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.apiAuditSummaryQuery, data, config);
};

// 获取资讯新动态数量
export const getResearchNewsCountApi = async (data, config = {}) => {
  return await httpInstance.post(researchNews.count, data, config);
};

// 标记资讯新动态已读
export const markResearchNewsReadApi = async (data, config = {}) => {
  return await httpInstance.post(researchNews.markRead, data, config);
};

/**
 * 资讯转存到研究模式文档
 * @param {{ taskId: number, tabType: number, sourceId: string }} data
 * tabType: 1 内资研报 / 2 外资研报 / 3 纪要 / 4 点评 / 5 公告
 * sourceId: reportId(研报) / summaryId(纪要) / id(点评) / annoId(公告)
 */
export const saveResearchNewsApi = async (data, config = {}) => {
  return await httpInstance.post(researchNews.save, data, config);
};

/**
 * 批量转存：同步将勾选的内容（≤50）逐条转存到当前资料夹，返回每条结果。
 * @param {object} data
 * @param {string|number} data.taskId 研究模型任务 ID
 * @param {Array<{ tabType: number, sourceId: string|number, name: string }>} data.items
 *   tabType: 1 内资研报 / 2 外资研报 / 3 纪要 / 4 点评 / 5 公告
 * @param {object} [config] 请求配置
 * @returns {Promise<{ code: string, msg?: string, data?: { successCount: number, failCount: number,
 *   items: Array<{ tabType: number, sourceId: string|number, status: number, statusDesc: string, nodeId: string|null }> } }>}
 *   status 对应 RESAVED_STATUS（0 成功 / 1 已转存 / 2 供应商不支持 / 3 格式不支持 / 4 去解锁 / 5 转存失败）
 */
export const batchResaveResearchNewsApi = async (data, config = {}) => {
  return await httpInstance.post(researchNews.batchResave, data, config);
};

/**
 * 动态弹框列表请求统一入参。
 * @typedef {object} ResearchNewsListParams
 * @property {string|number} taskId 研究模型任务 ID，用于回填每条内容的 resaved（是否已转存）状态
 * @property {number} page 页码，从 1 开始
 * @property {number} size 每页条数
 * @property {string} [industryTagIds] 行业 id（行业维度，与 fullCode 二选一）
 * @property {string} [fullCode] 个股完整代码（个股维度，与 industryTagIds 二选一）
 * @property {number} [orderByType] 排序方式：1 综合排序 / 2 最新发布 / 3 相关推荐
 * @property {number} [startTime] 开始时间（毫秒时间戳），不传由下游默认处理
 * @property {number} [endTime] 结束时间（毫秒时间戳），不传下游使用当前时间
 * @property {string} [keyword] 关键词搜索（轻量级，仅搜索列表字段）
 */

/**
 * 解析动态弹框列表接口响应（兼容 5 个下游「透传」的不同结构）。
 * @description 这 5 个接口透传各自下游，返回结构并不统一：
 *   - 列表字段：可能是 `rows` / `data`(数组) / `data.list`；
 *   - 翻页字段：可能是 `extra.hasMore`(布尔) / `total` / `extra.total` / `data.total`。
 *   故按优先级判断是否还有更多：`extra.hasMore` → 依据 total 比对 → 「满页即可能有更多」兜底，
 *   避免某些只返回 total 的接口因取不到 `extra.hasMore` 而误判为「没有更多」导致滚动不再加载。
 * @param {object} res 接口响应体
 * @param {number} [loadedCount=0] 本次拼接前已加载条数（用 total 判断时需要）
 * @param {number} [size=20] 每页条数（满页兜底判断用）
 * @returns {{ rows: Array<object>, hasMore: boolean }}
 */
export const parseResearchNewsListResult = (res, loadedCount = 0, size = 20) => {
  const raw = res?.rows ?? (Array.isArray(res?.data) ? res.data : res?.data?.list) ?? [];
  const rows = Array.isArray(raw) ? raw : [];
  // 翻页判断优先用 total：这些透传接口的 extra.hasMore/more 不可靠（实测常恒为 false），
  // 真正反映总量的是 total（可能在顶层 / extra / data 下，且可能是字符串）。
  const totalRaw = res?.total ?? res?.extra?.total ?? res?.data?.total;
  const hasTotal =
    (typeof totalRaw === 'number' || (typeof totalRaw === 'string' && totalRaw !== '')) &&
    Number.isFinite(Number(totalRaw));
  let hasMore;
  if (hasTotal) {
    hasMore = loadedCount + rows.length < Number(totalRaw);
  } else if (res?.extra && typeof res.extra.hasMore !== 'undefined') {
    hasMore = !!res.extra.hasMore;
  } else {
    // 兜底：满页即认为可能还有更多
    hasMore = rows.length >= size;
  }
  return { rows, hasMore };
};

/**
 * 动态弹框 - 内资研报列表（透传下游 + 回填是否已转存）。
 * @param {ResearchNewsListParams} data
 * @param {object} [config] 请求配置
 */
export const listDomesticResearchNewsApi = async (data, config = {}) => {
  return await httpInstance.post(researchNews.listDomesticResearch, data, config);
};

/**
 * 动态弹框 - 外资研报列表（透传下游 + 回填是否已转存）。
 * @param {ResearchNewsListParams} data
 * @param {object} [config] 请求配置
 */
export const listOverseaResearchNewsApi = async (data, config = {}) => {
  return await httpInstance.post(researchNews.listOverseaResearch, data, config);
};

/**
 * 动态弹框 - 纪要列表（透传下游 + 回填是否已转存）。
 * @param {ResearchNewsListParams} data
 * @param {object} [config] 请求配置
 */
export const listSummaryNewsApi = async (data, config = {}) => {
  return await httpInstance.post(researchNews.listSummary, data, config);
};

/**
 * 动态弹框 - 点评列表（透传下游 + 回填是否已转存）。
 * @param {ResearchNewsListParams} data
 * @param {object} [config] 请求配置
 */
export const listInsightsNewsApi = async (data, config = {}) => {
  return await httpInstance.post(researchNews.listInsights, data, config);
};

/**
 * 动态弹框 - 公告列表（透传下游 + 回填是否已转存）。
 * @param {ResearchNewsListParams} data
 * @param {object} [config] 请求配置
 */
export const listAnnouncementNewsApi = async (data, config = {}) => {
  return await httpInstance.post(researchNews.listAnnouncement, data, config);
};

// 提交节点重新进行 RAG 解析
export const ragSubmitApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.ragSubmit, data, config);
};

// 批量查询节点 RAG 解析状态
export const ragStatusApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.ragStatus, data, config);
};

/**
 * 获取研究模式数据范围（知识库）用户级设置，用于回显上次选择。
 *
 * 该设置只按用户维度保存、不区分会话；返回 `data.dataScopes` 为启用的数据范围能力 code 列表。
 * 新用户从未选择过时 `dataScopes` 为 null（上层按「默认全选除联网外」兜底，且不落库）。
 *
 * @param {object} [data] 预留入参（用户级，无需业务字段）
 * @param {object} [config] 请求配置
 * @returns {Promise<{ code: string, msg?: string, data?: { dataScopes: string[] | null } }>}
 */
export const getResearchKnowledgeSettingApi = async (data = {}, config = {}) => {
  return await httpInstance.post(aiResearchMode.getKnowledgeSetting, data, config);
};

/**
 * 研究模式文档分享解析（免鉴权）。
 * @param {{ shareCode: string }} data
 */
export const getDocumentShareDetailApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.documentShareResolve, data, config);
};

/**
 * 创建研究模式文档节点分享链接。
 * @param {{ docNodeId: string, allowDownload?: boolean, expireAt?: number, regenerate?: boolean }} data
 *  - docNodeId：文档节点 ID
 *  - allowDownload：是否允许匿名下载（后端据此在只读分享 config 放开 permissions.download）
 *  - expireAt：过期时间（毫秒时间戳），null / 0 表示不过期
 *  - regenerate：是否重新生成分享链接
 * @param {object} [config]
 */
export const createDocumentNodeShareApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.nodeShareCreate, data, config);
};

/**
 * 研究模式文档分享页免鉴权下载。
 * 后端按 shareCode 出下载信息：优先消费 `downloadUrl`（临时签名链接，前端直接导航下载）；
 * 无链接时再消费 `content` 内联内容。
 * @param {{ shareCode: string, downloadType: string }} data
 *  - shareCode：分享码
 *  - downloadType：下载格式，如 pdf / docx / xlsx / csv / pptx / md 等
 * @param {object} [config]
 * @returns {Promise<{ data?: { nodeId?: string, fileName?: string, fileExt?: string, mimeType?: string, downloadUrl?: string, content?: string } }>}
 */
export const getDocumentNodeShareDownloadApi = async (data, config = {}) => {
  return await httpInstance.get(aiResearchMode.nodeShareDownload, data, config);
};

/**
 * 保存研究模式数据范围（知识库）用户级设置（仅记录最后一次选择）。
 *
 * @param {{ dataScopes: string[] }} data 启用的数据范围能力 code 列表；空数组表示清空上次选择
 * @param {object} [config] 请求配置
 * @returns {Promise<{ code: string, msg?: string, data?: null }>}
 */
export const saveResearchKnowledgeSettingApi = async (data, config = {}) => {
  return await httpInstance.post(aiResearchMode.saveKnowledgeSetting, data, config);
};

/**
 * 批量下载（创建异步任务）
 * @param {{ nodeIds: string[] }} params
 * @param {object} [config] 请求配置
 * @returns {{ data: { taskId: number } }}
 */
export const batchDownloadNodeApi = async (params, config = {}) => {
  return await httpInstance.post(aiResearchMode.nodeBatchDownloadAsync, params, config);
};

/**
 * 查询批量下载任务状态（轮询）
 * @param {{ taskId: number }} params
 * @param {object} [config] 请求配置
 */
export const fetchBatchDownloadStatusApi = async ({ taskId }, config = {}) => {
  return await httpInstance.post(aiResearchMode.batchDownloadTask, void 0, { ...config, params: { taskId } });
};

/**
 * 批量查询下载任务详情（轮询用）
 * @param {{ batchTaskIds: number[] }} params
 * @param {object} [config] 请求配置
 */
export const fetchBatchDownloadTaskListApi = async (params, config = {}) => {
  return await httpInstance.post(aiResearchMode.batchDownloadTaskBatchQuery, params, config);
};

/**
 * 批量下载失败重试
 * @param {{ taskId: number }} params
 * @param {object} [config] 请求配置
 */
export const retryBatchDownloadApi = async ({ taskId }, config = {}) => {
  return await httpInstance.post(aiResearchMode.batchDownloadAsyncRetry, void 0, { ...config, params: { taskId } });
};

/**
 * 取消批量下载任务
 * @param {{ taskId: number }} params
 * @param {object} [config] 请求配置
 */
export const cancelBatchDownloadApi = async ({ taskId }, config = {}) => {
  return await httpInstance.post(aiResearchMode.batchDownloadAsyncCancel, void 0, { ...config, params: { taskId } });
};

/**
 * 批量删除文档节点
 * 注意：字段名是 ids，不是 nodeIds
 * @param {{ ids: string[] }} params
 * @param {object} [config] 请求配置
 */
export const batchDeleteNodeApi = async (params, config = {}) => {
  return await httpInstance.post(aiResearchMode.nodeBatchDelete, params, config);
};

/**
 * 批量移动预检（只读，不写入）
 * @param {{ nodeIds: string[], targetParentId: string }} params
 * @param {object} [config] 请求配置
 */
export const batchMoveCheckApi = async (params, config = {}) => {
  return await httpInstance.post(aiResearchMode.nodeBatchMoveCheck, params, config);
};

/**
 * 批量移动文档节点
 * @param {{ nodeIds: string[], targetParentId: string, prevSiblingId?: string }} params
 * @param {object} [config] 请求配置
 */
export const batchMoveNodeApi = async (params, config = {}) => {
  return await httpInstance.post(aiResearchMode.nodeBatchMove, params, config);
};

/**
 * 股票列表分页搜索（研究模式专用，仅返回在市且未删除记录）。
 * @param {{ pageNum?: number, pageSize?: number, keyword?: string }} data 查询参数
 * @param {object} [config] 请求配置
 * @returns {Promise<{ code: string, data: object[], extra: { hasMore: boolean } }>}
 */
export const searchResearchModeStockApi = async (data = {}, config = {}) => {
  return await httpInstance.post(aiResearchMode.searchResearchModeStock, data, config);
};

/**
 * 获取行业标签列表（研究模式专用，剔除"中小市值"、"海外研究"、"北交所"三个行业）。
 * @param {{ isResAllTag?: boolean }} [data] isResAllTag=true 含"全部"标签，false 不含（默认 false）
 * @param {object} [config] 请求配置
 * @returns {Promise<{ code: string, data: object[] }>}
 */
export const getResearchModeIndustryApi = async (data = {}, config = {}) => {
  return await httpInstance.post(aiResearchMode.getResearchModeIndustry, data, config);
};
